import { navigateHref } from "../../../features/navigation/navigation.ts";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppFeedback from "../AppFeedback.tsx";
import { getGridOperationContextActions } from "./GridOperationMenu.tsx";
import { isOperationColumn } from "./GridRowActions.tsx";

const interactive = (target) => target instanceof Element &&
  target.closest("button, a, input, textarea, select, [role='button'], [contenteditable='true']");
// Tree-data parents are real records too; synthetic groups/footers are not.
const usable = (node) => node?.data && !node.footer && !node.rowPinned && Number.isInteger(node.rowIndex);

export default function useGridRowActions(props, apiRef, loading) {
  const navigate = useNavigate();
  const [target, setTarget] = useState();
  const [selectedCount, setSelectedCount] = useState(0);
  const [pending, setPending] = useState();
  const busy = useRef(false);
  const pointerInteractive = useRef(false);
  const columns = props.columns?.filter(isOperationColumn) || [];
  const multiple = props.rowSelection === "multiple" || props.gridOptions?.rowSelection === "multiple" ||
    props.columns?.some((column) => column.checkboxSelection);
  const disabled = loading || props.actionsDisabled;
  // A refreshed/replaced data set invalidates the old command target immediately.
  const currentTarget = target?.source === props.data && usable(target.node) ? target.node : undefined;
  useEffect(() => {
    setTarget(undefined);
    setSelectedCount(0);
  }, [props.data]);

  function resolve(node, sample) {
    const row = node?.data || sample;
    if (!row) return [];
    const api = apiRef.current?.api;
    const actions = columns.flatMap((column, index) => {
      if (typeof column.cellRenderer !== "function") return [];
      const content = column.cellRenderer({
        data: row, node: node || { data: row, rowIndex: 0 }, api, columnApi: api, colDef: column,
        column: api?.getColumn(column.colId || column.field),
        context: props.gridOptions?.context,
      });
      return getGridOperationContextActions(content).map((action) => ({ ...action, key: `${index}:${action.key}` }));
    });
    return actions;
  }

  function clear() {
    setTarget(undefined);
    setSelectedCount(0);
    apiRef.current?.api?.deselectAll();
    apiRef.current?.api?.clearFocusedCell();
    apiRef.current?.api?.clearRangeSelection();
  }

  function activate(node) {
    if (!usable(node)) { setTarget(undefined); return; }
    setTarget({ node, source: props.data, rowIndex: node.rowIndex });
  }

  async function run(action, event, node = currentTarget) {
    if (disabled || busy.current || !usable(node) || (multiple && selectedCount > 1)) return;
    // Re-resolve against current data and permission guards, never an old closure.
    if (!props.data?.includes(node.data)) return;
    const latest = resolve(node).find((item) => item.key === action.key && item.label === action.label);
    if (!latest || latest.disabled) return;
    busy.current = true;
    setPending(latest.key);
    try {
      await latest.onClick?.(event || new MouseEvent("click"));
      if (latest.to) navigate(latest.to);
      else if (latest.href && latest.target === "_blank") window.open(latest.href, "_blank", "noopener,noreferrer");
      else if (latest.href) navigateHref(navigate, latest.href);
    } catch (error) {
      void AppFeedback.httpError(error);
    } finally {
      busy.current = false;
      setPending(undefined);
    }
  }

  return {
    hasOperations: columns.length > 0,
    actions: resolve(currentTarget, props.data?.[0]),
    rowNumber: currentTarget ? currentTarget.rowIndex + 1 : undefined,
    selectedCount: multiple && currentTarget ? selectedCount : 0,
    disabled: disabled || !currentTarget || (multiple && selectedCount > 1),
    pending, run, clear, resolve,
    onPointerDownCapture: (event) => { pointerInteractive.current = Boolean(interactive(event.target)); },
    onKeyDownCapture: () => { pointerInteractive.current = false; },
    events: {
      onCellClicked: (event) => { if (!interactive(event.event?.target)) activate(event.node); },
      onCellFocused: (event) => {
        if (!pointerInteractive.current && !event.rowPinned && Number.isInteger(event.rowIndex)) {
          activate(event.api.getDisplayedRowAtIndex(event.rowIndex));
        }
      },
      onCellKeyDown: (event) => {
        pointerInteractive.current = false;
        if (!interactive(event.event?.target)) activate(event.node);
      },
      onRangeSelectionChanged: (event) => {
        if (pointerInteractive.current) return;
        const ranges = event.api.getCellRanges();
        const row = ranges?.[ranges.length - 1]?.endRow;
        if (row && !row.rowPinned) activate(event.api.getDisplayedRowAtIndex(row.rowIndex));
      },
      onSelectionChanged: (event) => {
        if (!multiple) return;
        const nodes = event.api.getSelectedNodes().filter(usable);
        setSelectedCount(nodes.length);
        activate(nodes[0]);
      },
      onFilterChanged: clear,
      onRowDataUpdated: () => { setTarget(undefined); setSelectedCount(0); },
      onModelUpdated: () => {
        if (target && !usable(target.node)) { setTarget(undefined); setSelectedCount(0); }
        else if (currentTarget && target.rowIndex !== currentTarget.rowIndex) {
          setTarget({ ...target, rowIndex: currentTarget.rowIndex });
        }
      },
    },
  };
}
